import { get } from "lodash";

import LocalStoreManager from "./local-store-manager";
import ValidationHelper from "./validation-helper";
import QueryStringHelper from "./query-string-helper";
import PartnerRouteService from "./partner-route-service";
// Proxies
import GetCompanyProxy from "../proxies/back-end/company/get";
import GetCompanyServicesProxy from "../proxies/back-end/company-services/get";
import GetCompanyUserProxy from "../proxies/back-end/company-user/get";
import GetInvoiceProxy from "../proxies/back-end/invoices/get";
import InvoiceProxy from "../proxies/payment/invoice.proxy";

// Constants

import vueStore from "../store";

import { isPartner } from "../utilities/partner-util";
import { getPermittedRoles, validateUserPermission } from "../utilities/app-access-util";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import getPlatformConfig from "@/utilities/get-platform-config";
import { ACCOUNTING_QUESTIONNAIRE_ALLOWED_STATUSES_TO_ENABLE } from "@/mixins/dss-accessibility-mixin";
import { Constants } from "customer-sdk/constants";

const PAGE_URLS = Constants.PAGE_URLS;
const COMPANY_STATUSES = Constants.COMPANY_STATUSES;
const COMPANY_USER_ACCESS_ROLES = Constants.COMPANY_USER_ACCESS_ROLES;
const { SLEEK_APP_CACHE_KEY, SLEEK_DISABLE_CUSTOMER_APP } = Constants.STORE_CONSTANTS;
const { COMPANY_USER_MODULE, COMPANY_USERS, SET_COMPANY_USERS } = Constants.COMPANY_USERS_CONSTANTS;

let POST_ONBOARDING_AND_PRE_LIVE_COMPANY_STATUSES = [
  COMPANY_STATUSES.AWAITING_COMPANY_DETAIL.value,
  COMPANY_STATUSES.PAID_AND_INCOMPLETE.value,
  COMPANY_STATUSES.PROCESSING_BY_SLEEK.value,
  COMPANY_STATUSES.REFERRED_TO_ACRA.value,
  COMPANY_STATUSES.REFERRED_TO_ASIC.value,
  COMPANY_STATUSES.LIVE_POST_INCORPORATION.value,
  COMPANY_STATUSES.PROCESSING_INCORP_TRANSFER.value,
  COMPANY_STATUSES.AWAITING_COMPANY_DETAIL.value,
];

let STRIKE_AND_CHURN_STATUSES = [
  COMPANY_STATUSES.STRIKING_OFF_REQUESTED.value,
  COMPANY_STATUSES.STRIKING_OFF.value,
  COMPANY_STATUSES.STRIKED_OFF.value,
  COMPANY_STATUSES.CHURN_REQUESTED.value,
  COMPANY_STATUSES.CHURN_PROCESS.value,
  COMPANY_STATUSES.CHURN.value,
];

const strikeOffForbiddenRoutes = [
  PAGE_URLS.CORPSEC,
  PAGE_URLS.MAILROOM,
  PAGE_URLS.REQUEST,
  PAGE_URLS.ON_GOING_REQUEST,
  PAGE_URLS.HISTORY_OF_REQUEST,
  PAGE_URLS.BILLING,
  PAGE_URLS.BILLING_SUBSCRIPTIONS,
  PAGE_URLS.BILLING_INVOICES,
  PAGE_URLS.BILLING_PAYMENT_PREFERENCES,
  PAGE_URLS.ACCOUNTING_FILES,
  PAGE_URLS.ACCOUNTING_OPTIONS,
];

const churnForbiddenRoutes = [
  PAGE_URLS.CORPSEC,
  PAGE_URLS.MAILROOM,
  PAGE_URLS.REQUEST,
  PAGE_URLS.ON_GOING_REQUEST,
  PAGE_URLS.HISTORY_OF_REQUEST,
];

const fetchInvoices = async () => {
  try {
    const sleekBackInvoiceProxy = new GetInvoiceProxy();
    const microserviceInvoiceProxy = new InvoiceProxy();
    const store = new LocalStoreManager();
    const input = {
      authToken: store.getToken(),
      id: await store.getCompanyId(),
      companyId: await store.getCompanyId(),
    };
    const invoices = store.getMicroserviceEnable()
      ? await microserviceInvoiceProxy.getCompanyInvoices(input)
      : await sleekBackInvoiceProxy.getCompanyInvoices(input);

    return invoices;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log("Error in fetchInvoices:: ", fetchInvoices);
    return [];
  }
};

const checkCompanySoleProp = async (platformConfig, company) => {
  const admin_constants = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "admin_constants");
  const SOLE_PROP_SERVICES = getAppFeatureProp(admin_constants.props, "SOLE_PROP_SERVICES");
  const SOLE_PROP_CODES = getAppFeatureProp(admin_constants.props, "SOLE_PROP_CODES");

  const MAP_CONSTANTS = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "MAPS_CONSTANTS");
  const SUBSCRIPTION_STATUSES = getAppFeatureProp(MAP_CONSTANTS.props, "SUBSCRIPTION_STATUSES");
  const ACTIVE_SERVICE_STATUS = SUBSCRIPTION_STATUSES.value.ACTIVE;

  const solePropServices = SOLE_PROP_SERVICES.value || [];
  const solePropCodes = SOLE_PROP_CODES.value || [];

  let invoices = await fetchInvoices();
  let successfulSolePropInvoiceExists = invoices.find(
    (invoice) =>
      invoice.status == "done" && invoice.line_items.find((lineItem) => solePropCodes.includes(lineItem.ItemCode))
  );

  if (
    company.subscriptions.some(
      (subscription) => solePropServices.includes(subscription.service) && subscription.status === ACTIVE_SERVICE_STATUS
    ) ||
    successfulSolePropInvoiceExists
  ) {
    return true;
  }

  return false;
};

//#region Private Methods
let buildCompanyRequestPayload = async () => {
  let store = new LocalStoreManager();

  return {
    id: await store.getCompanyId(),
    authToken: store.getToken(),
  };
};

let getRedirectUrl = () => {
  let queryStringHelper = new QueryStringHelper();
  return queryStringHelper.get("redirect");
};

let getIsFromRejectedKyc = () => {
  let queryStringHelper = new QueryStringHelper();
  return queryStringHelper.get("isFromRejectedKyc");
};

let getIsFromInvitation = () => {
  let queryStringHelper = new QueryStringHelper();
  return queryStringHelper.get("isFromInvitation");
};

let getIsShowConsentPopup = () => {
  let queryStringHelper = new QueryStringHelper();
  return queryStringHelper.get("isShowConsentPopup");
};

let getRequestedCompanyId = () => {
  let queryStringHelper = new QueryStringHelper();
  return queryStringHelper.get("requestedCompanyId");
};

let getIsFromKycResubmissionRequest = () => {
  let queryStringHelper = new QueryStringHelper();
  return queryStringHelper.get("isFromKycResubmissionRequest");
};

let getCompanyUsers = async (id) => {
  let proxy = new GetCompanyUserProxy();
  let store = new LocalStoreManager();

  return await proxy.execute({ authToken: store.getToken(), id: id });
};
//#endregion

const redirectUrlPerStatus = async (status) => {
  const store = new LocalStoreManager();
  const companyStatus = store.getCompanyStatus();

  let { pathname } = window.location;
  if (pathname[pathname.length - 1] !== "/") {
    pathname = pathname + "/";
    window.location.href = pathname;
  }

  if (status.COMPANY_STATUSES_STRIKE.includes(companyStatus)) {
    localStorage.setItem(SLEEK_DISABLE_CUSTOMER_APP, false);

    if (strikeOffForbiddenRoutes.includes(pathname)) {
      window.location.href = PAGE_URLS.HOME;
    }
  } else if (status.COMPANY_STATUSES_CHURN.includes(companyStatus)) {
    localStorage.setItem(SLEEK_DISABLE_CUSTOMER_APP, false);

    const proxy = new GetCompanyServicesProxy();
    const response = await proxy.execute({
      id: await store.getCompanyId(),
      authToken: await store.getToken(),
    });

    const hasAccountingService = response.some((subscription) =>
      status.ACCOUNTING_SERVICES.includes(subscription.service)
    );

    if (hasAccountingService && churnForbiddenRoutes.includes(pathname)) {
      window.location.href = PAGE_URLS.HOME;
    }

    if ([PAGE_URLS.ACCOUNTING_FILES, PAGE_URLS.ACCOUNTING_OPTIONS, ...churnForbiddenRoutes].includes(pathname)) {
      window.location.href = PAGE_URLS.HOME;
    }
  }
};
/**
 * @class DefaultRouteEngine
 * @description Class used for asserting the default route for any user in Sleek UI v2.
 * @author Allan A. Chua
 * @version 1.0
 * @since Feb 02, 2019
 */

/*eslint complexity: "off"*/
export default class DefaultRouteEngine {
  constructor(pathname) {
    this.pathname = pathname ? pathname : window.location.pathname;
  }
  /**
   * @async
   * @function execute
   * @description Method used for executing the module.
   */
  /*eslint-disable */
  async execute(component, options = {}) {
    let store = new LocalStoreManager();

    let isEmailVerified = await store.checkIfEmailIsVerified();
    let isTwoFactor = await store.getIsTwoFactor();

    let queryStringHelper = new QueryStringHelper();
    let isFromDashboard = queryStringHelper.get("isFromDashboard");

    if (store.getToken()) {
      if (isTwoFactor) {
        return PAGE_URLS.TWO_FACTOR_AUTH;
      }
      if (isEmailVerified) {
        let payload = await buildCompanyRequestPayload();
        let validator = new ValidationHelper();
        let checkIsPartner = await isPartner();

        if (checkIsPartner) {
          let isPartnerAdmin = true;
          let isPartnerAdminValue = await store.getItem("isPartnerAdmin");
          if (!validator.getTruthyOrNull(isPartnerAdminValue) || isPartnerAdminValue == "false") {
            isPartnerAdmin = false;
          }
          if (isPartnerAdmin) {
            return PAGE_URLS.PARTNER.INDEX;
          } else {
            let partnerRouteService = new PartnerRouteService();
            return await partnerRouteService.routePartner(component, payload, options);
          }
        }

        if (
          this.pathname === PAGE_URLS.VERIFY_ACCOUNT ||
          this.pathname === PAGE_URLS.VERIFY ||
          this.pathname === PAGE_URLS.VERIFY_ACCOUNT.substr(0, 16)
        ) {
          window.location.href = PAGE_URLS.HOME;
        }

        if (validator.getTruthyOrNull(payload.id)) {
          let proxy = new GetCompanyProxy();
          let company = await proxy.execute(payload);
          let companyId = company._id || store.getCompanyId();
          if (company) {
            if (validator.getTruthyOrNull(company.partner)) {
              return `${PAGE_URLS.DASHBOARD}${companyId}`;
            }

            if (getRedirectUrl()) {
              let url = new URL(getRedirectUrl());
              return url.pathname + url.search;
            }

            const platformConfig = await getPlatformConfig(window.location.hostname);
            const new_dashboard_onboarding_enabled = getAppFeatureProp(
              platformConfig.cmsAppFeatures,
              "new_dashboard_onboarding_enabled"
            );

            const isNewPostPaymentDashboardEnabled = new_dashboard_onboarding_enabled.enabled;
            localStorage.setItem(SLEEK_DISABLE_CUSTOMER_APP, get(company, "is_customer_app_disabled", false));

            if (isNewPostPaymentDashboardEnabled && company.is_dashboard_launched) {
              if (!isFromDashboard) {
                return `${PAGE_URLS.DASHBOARD}${companyId}`;
              }
            }

            const admin_constants = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "admin_constants");
            await redirectUrlPerStatus(admin_constants.value);

            if (localStorage.getItem(SLEEK_DISABLE_CUSTOMER_APP) == "true") {
              if (
                this.pathname !== PAGE_URLS.INCORPORATION &&
                this.pathname !== PAGE_URLS.ONBOARDING &&
                this.pathname !== PAGE_URLS.HOME &&
                this.pathname !== PAGE_URLS.BILLING
              ) {
                return `${PAGE_URLS.DASHBOARD}${companyId}`;
              }
            }

            let companyUsers = await getCompanyUsers(companyId);
            vueStore.dispatch(`${COMPANY_USER_MODULE}/${SET_COMPANY_USERS}`, companyUsers);

            if (component) {
              companyUsers = component.$store.getters[`${COMPANY_USER_MODULE}/${COMPANY_USERS}`];
            } else {
              const sleekAppCacheKey = (await localStorage.getItem(SLEEK_APP_CACHE_KEY)) || [];
              if (sleekAppCacheKey) {
                if (sleekAppCacheKey.length > 0) {
                  let parsedSleekAppCache = JSON.parse(sleekAppCacheKey);
                  if (
                    parsedSleekAppCache &&
                    parsedSleekAppCache.companyUsersModule &&
                    parsedSleekAppCache.companyUsersModule.companyUsers
                  ) {
                    companyUsers = parsedSleekAppCache.companyUsersModule.companyUsers;
                  }
                }
              } else {
                store.logout();
              }
            }
            let userId = await store.getUserId();

            const permittedRoles = getPermittedRoles([
              COMPANY_USER_ACCESS_ROLES.OWNER,
              COMPANY_USER_ACCESS_ROLES.DIRECTOR,
              COMPANY_USER_ACCESS_ROLES.SHAREHOLDER,
            ]);
            const hasPostPaymentAccess = validateUserPermission(userId, companyUsers, permittedRoles);
            const companyOwner = validateUserPermission(userId, companyUsers, [COMPANY_USER_ACCESS_ROLES.OWNER]);

            if (!hasPostPaymentAccess && !store.getIsAdmin()) {
              return `${PAGE_URLS.DASHBOARD}${companyId}`;
            }

            let companyInvoices = [];
            if (company.microservice_enabled) {
              companyInvoices = await fetchInvoices();
            }

            if (company.status === COMPANY_STATUSES.DRAFT.value) {
              if (
                validator.getTruthyOrNull(company.expected_external_payment_info) ||
                (company.microservice_enabled && companyInvoices.length > 0)
              ) {
                let redirectUrl = isNewPostPaymentDashboardEnabled
                  ? PAGE_URLS.INCORPORATION
                  : `${PAGE_URLS.PAYMENT_PROCESSING}?cid=${company._id}`;

                return redirectUrl;
              } else {
                if (companyOwner) {
                  return `${PAGE_URLS.ONBOARDING}?cid=${company._id}`;
                } else {
                  return `${PAGE_URLS.DASHBOARD}${company._id}`;
                }
              }
            }

            /* if(["live_post_incorporation"].includes(company.status)){
                return PAGE_URLS.INCORPORATION_ACCOUNTING_SETUP;
              } else {*/

            if (
              ACCOUNTING_QUESTIONNAIRE_ALLOWED_STATUSES_TO_ENABLE.includes(company.status) &&
              store.isAccountingSetupPageAccessed(company._id)
            ) {
              if (companyOwner) {
                return PAGE_URLS.ACCOUNTING_SETUP;
              } else {
                return PAGE_URLS.INCORPORATION;
              }
            }

            if (POST_ONBOARDING_AND_PRE_LIVE_COMPANY_STATUSES.findIndex((status) => company.status === status) > -1) {
              return PAGE_URLS.INCORPORATION;
            }

            if (company.status === COMPANY_STATUSES.LIVE.value) {
              let isCompanySoleProp = await checkCompanySoleProp(platformConfig, company);
              if (
                (isNewPostPaymentDashboardEnabled && !company.is_dashboard_launched && !isCompanySoleProp) ||
                isFromDashboard
              ) {
                if (getIsShowConsentPopup() === "true" && getRequestedCompanyId()) {
                  const url = new URL(window.location.href);
                  return PAGE_URLS.INCORPORATION + url.search;
                }
                return PAGE_URLS.INCORPORATION;
              }
              if (!isFromDashboard) {
                return `${PAGE_URLS.DASHBOARD}${companyId}`;
              }
            }

            if (company.status === COMPANY_STATUSES.ARCHIVED.value) {
              return `${PAGE_URLS.DASHBOARD}${companyId}`;
            }

            if (STRIKE_AND_CHURN_STATUSES.findIndex((status) => company.status === status) > -1) {
              return `${PAGE_URLS.DASHBOARD}${companyId}`;
            }

            return `${PAGE_URLS.DASHBOARD}${companyId}`;
          }
        }

        if (this.pathname === PAGE_URLS.INCORPORATION) {
          return PAGE_URLS.INCORPORATION;
        }

        return PAGE_URLS.ONBOARDING;
      } else {
        return PAGE_URLS.VERIFY_ACCOUNT;
      }
    } else {
      const redirectUrl = getRedirectUrl();
      const isFromRejectedKyc = getIsFromRejectedKyc() === "true";
      const isFromInvitation = getIsFromInvitation() === "true";
      const isFromKycResubmissionRequest = getIsFromKycResubmissionRequest() === "true";
      const isShowConsentPopup = getIsShowConsentPopup() === "true";
      const requestedCompanyId = getRequestedCompanyId();
      const isBusinessAccountPath = window.location.pathname.startsWith(PAGE_URLS.BUSINESS_ACCOUNT.slice(0, -1));

      if (isFromRejectedKyc && redirectUrl) {
        return `${PAGE_URLS.LOGIN}?redirect=${redirectUrl}`;
      } else if (
        (isFromInvitation && isFromKycResubmissionRequest) ||
        (isShowConsentPopup && requestedCompanyId) ||
        isBusinessAccountPath
      ) {
        return `${PAGE_URLS.LOGIN}?redirect=${window.location.href}`;
      }
      return `${PAGE_URLS.LOGIN}?redirect=${window.location.href}`;
    }
  }
}
